// =============================================================================
// Copyright   : 2019 semantics GmbH
// Date        : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
// Author      : Olli Powroznik
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 159808 $
// Purpose     : extended custom styled XMenu - Toolbar with dynamic space management
// =============================================================================

// ---Third-party imports
import React, {useCallback, useRef, useState, useEffect, useMemo} from 'react'
import clsx from 'clsx'

// ---Framework imports
import {useDimensions, useSelector, useUserSetting} from 'uix/hooks'
import log from 'uix/logging'
import {VIcon} from 'uix/graphics'
import {Sel} from 'uix/registries'

// ---Local imports
import {XMenu} from './menu'
import {DebugFeature} from './debug'
import styles from './toolbar.scss'

const Toolbar = ({
  responsive = false, // children = Toolbar.Group for best results ;
  attached = false, // true = "top"
  titled = true, // show/hide Toolbar.Group Titles
  ribbons = false, // shows Group title below
  vertical = false, // vertical Menu
  children,
  floated,
  disabled,
  //style
  colored = false,
  size = 'default', // mini, tiny, small, default, large
  className,
  ...props
}) => {
  let [headerStyle] = useUserSetting('headerStyle')
  if (headerStyle == 'compact') {
    ribbons = false
  }

  let childRefs = useRef([])
  let scrollRef = useRef([])
  let [toolbarDim, dimRef] = useDimensions({delta: 15})
  let [groupStates, setGroupStates] = useState([])
  let [overflow, setOverflow] = useState(false)
  let [hiddenItemsState, setHiddenItemsState] = useState({})

  let resolveAttach = () => {
    if (attached === true && !vertical) return 'top'
    if (vertical && attached === true) return 'left'
    return attached
  }

  let attachClass =
    {
      top: styles.topAttach,
      bottom: styles.bottomAttach,
      left: styles.leftAttach,
      right: styles.rightAttach,
      notAttach: styles.notAttach,
    }[resolveAttach()] || styles.notAttach

  let sizeClass = {
    mini: styles.mini,
    tiny: styles.tiny,
    small: styles.small,
    default: styles.default,
    large: styles.large,
    huge: styles.huge,
  }[size]

  // Helper to update hiddenItemsState
  let updateHiddenItems = useCallback((groupId, newHiddenCount, isCollapsed = false) => {
    setHiddenItemsState(prevHidden => {
      let currentState = prevHidden[groupId] || {}
      // Update nur bei Änderungen
      if (currentState.hiddenCount !== newHiddenCount || currentState.isCollapsed !== isCollapsed) {
        return {
          ...prevHidden,
          [groupId]: {hiddenCount: newHiddenCount, isCollapsed},
        }
      }
      return prevHidden
    })
  }, [])

  // check if all groups are collapsed still overflowing container
  let checkOverflow = availableSpace => {
    let allGroupsProcessed = groupStates.every(group => {
      let {hiddenCount, totalItems, isCollapsed} = groupDetails[group.id]
      // check if all groups are max. possible collapsed
      return hiddenCount >= totalItems - 2 || isCollapsed
    })

    return allGroupsProcessed && availableSpace < 0
  }

  // Helper for group details
  const calculateGroupDetails = (groupStates, hiddenItemsState) => {
    return groupStates.map((group, index) => {
      let isCollapsed = hiddenItemsState[index]?.isCollapsed
      let hiddenCount = hiddenItemsState[group.id]?.hiddenCount || 0
      let totalItems = group.childDimensions.length
      return {group, hiddenCount, totalItems, isCollapsed}
    })
  }

  let {availableSpace, groupDetails} = useMemo(() => {
    const groupsWidth = groupStates.reduce((acc, group) => acc + (group?.itemWidth || 0), 0)
    const availableSpace =
      responsive && groupStates.length > 0 ? Math.round(toolbarDim.width - groupsWidth) || 0 : 0

    const groupDetails = calculateGroupDetails(groupStates, hiddenItemsState)

    return {availableSpace, groupDetails}
  }, [toolbarDim.width, groupStates, hiddenItemsState])

  // Decrease visible items when there is not enough space
  let hideItems = availableSpace => {
    let groupIndex = groupStates.length - 1 // Start at the last group
    let shouldCollapse = true
    let minVisibleItems = 2
    // hide toolbar items
    while (groupIndex >= 0 && availableSpace < 0) {
      let {group, hiddenCount, totalItems} = groupDetails[groupIndex]
      let allowedWidth = group.fontSize * 3

      // let groups collapse
      if (ribbons && totalItems <= minVisibleItems) {
        if (totalItems === 1 && hiddenCount === 0) {
          if (group.childDimensions[0] > allowedWidth) {
            updateHiddenItems(group.id, totalItems, true)
          }
        } else {
          if (group.childDimensions[0] + group.childDimensions[1] > allowedWidth) {
            updateHiddenItems(group.id, totalItems, true)
          }
        }
      }
      if (hiddenCount < totalItems - minVisibleItems) {
        // Hide 2 items initially to offset the "More" button's space.
        let newHiddenCount = hiddenCount === 0 ? 2 : hiddenCount + 1
        if (totalItems - newHiddenCount === 1) {
          updateHiddenItems(group.id, totalItems, true)
          break
        }
        updateHiddenItems(group.id, newHiddenCount)
        shouldCollapse = false
        break
      }
      groupIndex--
    }

    if (shouldCollapse && ribbons) {
      groupIndex = groupStates.length - 1 // Reset groupIndex to start from the last group again

      while (groupIndex >= 0 && availableSpace < 0) {
        let {group, hiddenCount, totalItems} = groupDetails[groupIndex]

        if (totalItems <= minVisibleItems) {
          groupIndex--
          continue
        }

        // collapse group if all items hidden
        if (hiddenCount === totalItems - minVisibleItems || totalItems == 3) {
          updateHiddenItems(group.id, totalItems, true) // Set collapse to true
        }

        groupIndex--
      }
    }
  }

  let showItems = availableSpace => {
    let groupIndex = 0
    while (groupIndex < groupStates.length && availableSpace > 0) {
      let {group, hiddenCount, totalItems, isCollapsed} = groupDetails[groupIndex]
      if (hiddenCount > 0 || isCollapsed) {
        // Get width of the next hidden item
        let nextItemWidth = group.childDimensions[totalItems - hiddenCount]

        // Check if only two items are hidden
        if (hiddenCount >= 2) {
          // Get the width of the next two hidden items
          let secondNextItemWidth =
            hiddenCount > 1 ? group.childDimensions[totalItems - hiddenCount + 1] : 0

          // Check if both items fit in the available space
          if (availableSpace - (nextItemWidth + secondNextItemWidth) >= 20) {
            // Enough space to display both items
            updateHiddenItems(group.id, hiddenCount - 2)
            availableSpace -= nextItemWidth + secondNextItemWidth
          } else {
            // Not enough space for both -> check next group
            groupIndex++
          }
        } else {
          // Standard logic: Check if there is enough space for the next item
          if (availableSpace - nextItemWidth >= 20) {
            let newHiddenItems = hiddenCount - 1
            updateHiddenItems(group.id, newHiddenItems)

            // Deduct the width of the item from available space
            availableSpace -= nextItemWidth
          } else {
            groupIndex++
          }
        }
      } else {
        // No hidden items in this group, go to the next group
        groupIndex++
      }
    }
  }

  let setResponsiveGroup = (id, itemWidth, childDimensions, fontSize) => {
    setGroupStates(prev => {
      let updatedStates = [...prev]
      updatedStates[id] = {
        id,
        itemWidth,
        childDimensions: childDimensions || [],
        fontSize: fontSize || 0,
      }
      return updatedStates
    })
  }

  useEffect(() => {
    if (responsive && groupStates.length > 0) {
      setOverflow(checkOverflow(availableSpace))
      if (availableSpace < 0) {
        hideItems(availableSpace)
      } else if (availableSpace > 0) {
        showItems(availableSpace)
      }
    }
  }, [availableSpace])

  // Helper function to process children
  let responsiveChildren = (
    children,
    {hiddenItemsState, setResponsiveGroup, childRefs, styles}
  ) => {
    let validIdx = 0 // Counter for ToolbarGroups

    return (
      Array.isArray(children) &&
      children.map((child, i) => {
        // Handle DebugFeature wrapper
        if (child?.type?.displayName === 'DebugFeature') {
          let firstChild = Array.isArray(child.props.children)
            ? child.props.children[0]
            : child.props.children

          if (firstChild?.type?.displayName === 'ToolbarGroup') {
            let id = validIdx
            let hiddenItems = hiddenItemsState[id]?.hiddenCount || 0
            let isCollapsed = hiddenItemsState[id]?.isCollapsed || false
            validIdx++

            return (
              <DebugFeature key={`debug-${i}`}>
                {React.cloneElement(firstChild, {
                  id: id,
                  setResponsiveGroup,
                  hiddenItemsCount: hiddenItems,
                  isCollapsed,
                  style: firstChild.props.style,
                  disabled: disabled,
                })}
              </DebugFeature>
            )
          }
        }

        if (child?.type?.displayName === 'ToolbarGroup') {
          let id = validIdx
          let hiddenItems = hiddenItemsState[id]?.hiddenCount || 0
          let isCollapsed = hiddenItemsState[id]?.isCollapsed || false
          validIdx++

          return (
            <div
              title={child.props.title}
              key={i}
              style={child.props.style}
              className={clsx(
                child?.props?.position === 'right' && styles.right,
                styles.responsive
              )}
              ref={el => (childRefs.current[id] = el)}
            >
              {React.cloneElement(child, {
                id: id,
                setResponsiveGroup,
                hiddenItemsCount: hiddenItems,
                isCollapsed,
                disabled: disabled,
                style: child.props.style,
              })}
            </div>
          )
        }
        return null
      })
    )
  }

  return (
    <div style={{width: responsive ? '100%' : 'inherit'}} ref={dimRef}>
      <XMenu
        vertical={vertical}
        attached={attached ? true : false}
        floated={floated}
        size={size === 'default' ? null : size}
        className={clsx(
          className,
          styles.toolbar,
          attachClass,
          sizeClass,
          overflow && styles.overflowing,
          ribbons && styles.ribbons,
          !titled && styles.hideTitle,
          vertical && styles.vertical,
          floated === 'right' && styles.floated,
          colored && styles.colored
        )}
        {...props}
      >
        {responsive ? (
          <div className={styles.scrollContainer} ref={scrollRef}>
            {overflow && (
              <OverflowScrollButtons scrollRef={scrollRef} toolbarWidth={toolbarDim.width} />
            )}
            {responsiveChildren(children, {
              hiddenItemsState,
              setResponsiveGroup,
              childRefs,
              disabled: disabled,
              styles,
            })}
          </div>
        ) : (
          children
        )}
      </XMenu>
    </div>
  )
}

const OverflowScrollButtons = ({scrollRef, toolbarWidth}) => {
  let [isScrollableLeft, setIsScrollableLeft] = useState(false)
  let [isScrollableRight, setIsScrollableRight] = useState(false)
  let offset = toolbarWidth - 45 || 200

  useEffect(() => {
    if (scrollRef.current) {
      handleScroll()
      scrollRef.current.addEventListener('scroll', handleScroll)
    }
    return () => {
      if (scrollRef.current) {
        scrollRef.current.removeEventListener('scroll', handleScroll)
      }
    }
  }, [scrollRef, toolbarWidth])

  let handleScroll = () => {
    if (scrollRef.current) {
      let currentScrollLeft = scrollRef.current.scrollLeft
      let maxScroll = scrollRef.current.scrollWidth - toolbarWidth
      setIsScrollableLeft(currentScrollLeft > 0)
      setIsScrollableRight(currentScrollLeft < maxScroll)
    }
  }

  let scroll = direction => {
    scrollRef.current.scrollLeft += direction === 'right' ? offset : -offset
  }

  return (
    <>
      <div
        onClick={isScrollableLeft ? () => scroll('left') : null}
        className={clsx(styles.scrollButton, styles.left, !isScrollableLeft && styles.disabled)}
      >
        <VIcon disabled={!isScrollableLeft} name="chevron left" />
      </div>
      <div
        onClick={isScrollableRight ? () => scroll('right') : null}
        className={clsx(styles.scrollButton, styles.right, !isScrollableRight && styles.disabled)}
      >
        <VIcon disabled={!isScrollableRight} name="chevron right" />
      </div>
    </>
  )
}

// needed by ghost-toolbar items preventing cloned interaction
const stopInteraction = children => {
  return React.Children.map(children, (child, i) => {
    if (!React.isValidElement(child)) return child

    let displayName = child.type?.displayName || child.type?.name

    if (displayName) {
      return React.cloneElement(child, {key: `ghost-${i}`, disabled: true, open: false, on: []})
    }

    let newProps = {}
    if (child.props?.children) {
      newProps.children = stopInteraction(child.props.children)
    }

    return React.cloneElement(child, newProps)
  })
}

const flattenChildren = (children, exclude = []) => {
  if (!children) return []
  children = Array.isArray(children) ? children : [children]

  return children.reduce((flattened, child) => {
    if (!child) return flattened
    let displayName = child.type?.displayName

    // Keep DebugFeature as Wrapper
    if (displayName === 'DebugFeature') {
      return flattened.concat(
        <DebugFeature key={child.key || `debug-${Math.random()}`}>
          {flattenChildren(child.props.children, exclude)}
        </DebugFeature>
      )
    }
    // stop flatten on excluded displayNames
    let excluding = exclude.includes(displayName)
    if (excluding) {
      return flattened.concat(child)
    }

    // if no child → continue recursive flatten
    if (child?.props?.children) {
      return flattened.concat(flattenChildren(child.props.children, exclude))
    }

    return flattened.concat(child)
  }, [])
}

const Group = ({
  position, // "right" float right
  separator = true, // toggle this separators
  children,
  title,
  icon,
  style,
  show = true, // hides group
  //responsive Toolbar:
  id,
  setResponsiveGroup,
  hiddenItemsCount,
  isCollapsed,
  disabled,
}) => {
  let dimRef = useRef()
  let childRefs = useRef([])
  let flatChildren = flattenChildren(children, ['ToolbarMore', 'XPopupItem'])

  let [fontSize, setFontSize] = useState([])
  let [groupDimension, setGroupDimensions] = useState([])
  let childDimensions = childRefs.current.map(ref => ref?.getBoundingClientRect()?.width || 0)

  useEffect(() => {
    if (setResponsiveGroup) {
      let styles = window?.getComputedStyle(dimRef?.current)
      styles ? setFontSize(parseFloat(styles?.fontSize)) : '16'
    }
  }, [])

  useEffect(() => {
    if (setResponsiveGroup) {
      let childDimensions = childRefs.current.map(ref => ref?.getBoundingClientRect()?.width || 0)
      setResponsiveGroup(id, groupDimension, childDimensions, fontSize)
    }
  }, [groupDimension])

  useEffect(() => {
    if (setResponsiveGroup) {
      setGroupDimensions(Math.round(dimRef.current?.getBoundingClientRect().width) || 0)
    }
  }, [flatChildren, hiddenItemsCount])

  let visibleItems =
    hiddenItemsCount > 0
      ? flatChildren.slice(0, flatChildren.length - hiddenItemsCount)
      : flatChildren
  let hiddenItems = hiddenItemsCount > 0 ? flatChildren.slice(-hiddenItemsCount) : []

  let countActiveItems = items => {
    let count = 0
    let checkProps = props => {
      if (props.active === true || props.value === true) {
        count++
      }
      if (props.children) {
        if (Array.isArray(props.children)) {
          props.children.forEach(child => {
            if (child.props) checkProps(child.props)
          })
        } else if (props.children.props) {
          checkProps(props.children.props)
        }
      }
    }
    items.forEach(item => {
      if (item) checkProps(item)
    })
    return count
  }

  let activeItems = countActiveItems(hiddenItems.map(child => child?.props))
  let activeCollapsedItems = countActiveItems(flatChildren.map(child => child?.props))

  let responsiveContent = (
    <>
      {visibleItems.map((child, i) => React.cloneElement(child, {key: i, disabled}))}
      {hiddenItems.length > 0 && (
        <More disabled={disabled} activeItems={activeItems}>
          {flattenChildren(hiddenItems)}
        </More>
      )}
    </>
  )
  if (!show) return null

  return (
    <>
      {!setResponsiveGroup ? (
        <div style={style} className={clsx(styles.group, position === 'right' && styles.right)}>
          <div className={styles.menu}>
            {children}
            {separator && (
              <XMenu.Separator className={clsx(!separator && styles.hidden, styles.separator)} />
            )}
          </div>
          {title && <span className={styles.title}>{title}</span>}
        </div>
      ) : (
        <>
          <div
            ref={dimRef}
            key={id}
            style={style}
            className={clsx(styles.group, position === 'right' && styles.right)}
          >
            <div className={styles.menu}>
              {isCollapsed ? (
                <More
                  disabled={
                    disabled || flattenChildren(hiddenItems).every(child => child?.props?.disabled)
                  }
                  prominent
                  activeItems={activeCollapsedItems}
                  icon={icon}
                >
                  {flattenChildren(hiddenItems, 'XPopupItem')}
                </More>
              ) : (
                <>
                  {responsiveContent}
                  <XMenu.Separator
                    className={clsx(!separator && styles.hidden, styles.separator)}
                  />
                </>
              )}
            </div>
            {title && <span className={styles.title}>{title}</span>}
          </div>
          {/* responsive: measure invisible children widths - make visible in scss */}
          <div
            className={clsx(styles.group, styles.ghost)}
            style={{pointerEvents: 'none', userSelect: 'none'}}
          >
            <div className={styles.menu}>
              {stopInteraction(flatChildren).map((child, i) => (
                <div key={i} ref={el => (childRefs.current[i] = el)}>
                  {childDimensions.length > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        fontSize: '0.8em',
                        bottom: '-5px',
                      }}
                    >
                      {Math.round(childDimensions[i])}
                    </span>
                  )}
                  {React.cloneElement(child)}
                </div>
              ))}
            </div>
            {groupDimension && <span>{groupDimension}</span>}
          </div>
        </>
      )}
    </>
  )
}

const More = ({
  children,
  icon,
  caption,
  activeItems,
  prominent,
  title,
  vertical = false,
  position = 'bottom left',
  closeOnChildClick = false,
  ...props
}) => {
  let i18n = useSelector(Sel.i18n)
  return (
    <div title={title || i18n.get('more')} className={styles.moreItem}>
      <XMenu.PopupItem
        basic
        renderAlways
        position={position}
        className={clsx(styles.more, icon && styles.customIcon, prominent && styles.prominent)}
        icon={icon ? icon : 'ellipsis horizontal'}
        closeOnChildClick={closeOnChildClick}
        prominent={prominent}
        closeOnPopupDescendantClick={false}
        {...props}
      >
        <XMenu
          vertical={vertical}
          attached={false}
          className={clsx(styles.toolbar, styles.group, styles.more)}
        >
          {children}
        </XMenu>
      </XMenu.PopupItem>
      {activeItems > 0 && <span className={styles.badge}>{activeItems}</span>}
    </div>
  )
}

const ItemList = ({children}) => <div className={styles.itemList}>{children}</div>

// export
// rename out of usability reasons:
Toolbar.Item = XMenu.Item
Toolbar.Separator = ({className, props}) => (
  <XMenu.Separator className={clsx(styles.separator, className)} {...props} />
)

Toolbar.Group = Group
Toolbar.ItemList = ItemList
Toolbar.More = More

// set displayNames
Group.displayName = 'ToolbarGroup'
More.displayName = 'ToolbarMore'
ItemList.displayName = 'ToolbarItemList'

export {Toolbar}
